import { useApolloClient } from '@apollo/client';

import {
  useListAnswersQuery,
  useCreateAnswerMutation,
  ContextType,
  ListAnswersDocument,
  AnswerEdge,
} from '@/@generated/codegen/API';
import { Answer } from '@/contexts/MasterclassContext';

export const ANSWERS_QUERY_KEYS = {
  byUserId: (userId: string) => ['answers', 'user', userId] as const,
};

/**
 * Build the where clause for answers query, filtered by masterclass.
 * Exported for use in cache operations.
 */
export const buildAnswersWhereClause = (userId: string, masterclassId: string) => ({
  userId: { equals: userId },
  masterClassUserSessionId: { not: { equals: null } },
  // Filter by masterclass to prevent cross-contamination between masterclasses sharing the same case
  masterClassUserSession: {
    is: {
      masterClassId: { equals: masterclassId },
    },
  },
});

/**
 * Hook to query answers for a user, filtered by masterclass.
 *
 * When masterclassId is provided, only answers for that specific masterclass are fetched.
 * This prevents cross-contamination when the same patientCase is used in multiple masterclasses.
 * When masterclassId is null/undefined, no answers are fetched (lazy loading).
 *
 * @param userId - The user's ID
 * @param masterclassId - Masterclass ID to filter answers. When null, no fetch occurs.
 */
export const useAnswers = (userId: string | null, masterclassId?: string | null) => {
  const { data, loading, error, refetch } = useListAnswersQuery({
    variables: {
      where: userId && masterclassId ? buildAnswersWhereClause(userId, masterclassId) : {},
    },
    // Only fetch when both userId AND masterclassId are provided (lazy loading)
    skip: !userId || !masterclassId,
  });

  // Transform to match existing interface (Answer type from MasterclassContext)
  // Note: createdAt from GraphQL is already a string, not a Date object
  const transformedData = data?.listAnswers?.edges.map((edge) => ({
    id: edge.node.id,
    caseProgressId: edge.node.masterClassUserSessionId || '',
    slideId: edge.node.slideId,
    userId: edge.node.userId,
    answerId: edge.node.answerIndexes?.[0]?.toString() || '',
    caseId: edge.node.patientCaseId || '',
    createdAt:
      typeof edge.node.createdAt === 'string' ? edge.node.createdAt : String(edge.node.createdAt),
  }));

  return {
    data: transformedData,
    isLoading: loading,
    error,
    refetch,
  };
};

/**
 * Hook to insert a new answer.
 *
 * Uses optimistic updates to immediately show the answer in the UI.
 * Requires masterclassId to properly scope cache operations.
 */
export const useInsertAnswer = () => {
  const apolloClient = useApolloClient();
  const [createAnswer] = useCreateAnswerMutation();

  return {
    mutateAsync: async (variables: {
      caseProgressId: string;
      slideId: string;
      userId: string;
      answerId: string;
      caseId: string;
      masterclassId: string;
    }) => {
      // Create optimistic answer for immediate UI update
      const tempId = `temp-${Date.now()}`;
      const optimisticAnswer: Answer = {
        id: tempId,
        caseProgressId: variables.caseProgressId,
        slideId: variables.slideId,
        userId: variables.userId,
        answerId: variables.answerId,
        caseId: variables.caseId,
        createdAt: new Date().toISOString(),
      };

      // Optimistically update cache - must match useAnswers query variables
      const queryVariables = {
        where: buildAnswersWhereClause(variables.userId, variables.masterclassId),
      };

      const cachedData = apolloClient.readQuery({
        query: ListAnswersDocument,
        variables: queryVariables,
      });

      if (cachedData) {
        apolloClient.writeQuery({
          query: ListAnswersDocument,
          variables: queryVariables,
          data: {
            listAnswers: {
              ...cachedData.listAnswers,
              edges: [
                ...cachedData.listAnswers.edges,
                {
                  __typename: 'AnswerEdge',
                  cursor: tempId,
                  node: {
                    __typename: 'Answer',
                    id: tempId,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    slideId: variables.slideId,
                    patientCaseId: variables.caseId,
                    userId: variables.userId,
                    meetingId: null,
                    masterClassUserSessionId: variables.caseProgressId,
                    answerIndexes: [variables.answerId],
                    answerTexts: null,
                  },
                },
              ],
            },
          },
        });
      }

      try {
        // Create the actual answer
        const { data } = await createAnswer({
          variables: {
            data: {
              slideId: variables.slideId,
              patientCaseId: variables.caseId,
              contextType: ContextType.Masterclass,
              objId: variables.caseProgressId,
              answerIndexes: {
                set: [variables.answerId],
              },
            },
          },
        });

        // Replace temp answer with real one
        const updatedCachedData = apolloClient.readQuery({
          query: ListAnswersDocument,
          variables: queryVariables,
        });

        if (updatedCachedData) {
          apolloClient.writeQuery({
            query: ListAnswersDocument,
            variables: queryVariables,
            data: {
              listAnswers: {
                ...updatedCachedData.listAnswers,
                edges: updatedCachedData.listAnswers.edges.map((edge: AnswerEdge) =>
                  edge.node.id === tempId
                    ? {
                        __typename: 'AnswerEdge',
                        cursor: data!.createAnswer.id,
                        node: data!.createAnswer,
                      }
                    : edge
                ),
              },
            },
          });
        }

        // Transform to match interface
        // Note: createdAt from GraphQL is already a string
        const createdAt = data!.createAnswer.createdAt;
        return {
          id: data!.createAnswer.id,
          caseProgressId: data!.createAnswer.masterClassUserSessionId || '',
          slideId: data!.createAnswer.slideId,
          userId: data!.createAnswer.userId,
          answerId: data!.createAnswer.answerIndexes?.[0]?.toString() || '',
          caseId: data!.createAnswer.patientCaseId || '',
          createdAt: typeof createdAt === 'string' ? createdAt : String(createdAt),
        };
      } catch (error) {
        // On error, remove optimistic update
        const errorCachedData = apolloClient.readQuery({
          query: ListAnswersDocument,
          variables: queryVariables,
        });

        if (errorCachedData) {
          apolloClient.writeQuery({
            query: ListAnswersDocument,
            variables: queryVariables,
            data: {
              listAnswers: {
                ...errorCachedData.listAnswers,
                edges: errorCachedData.listAnswers.edges.filter(
                  (edge: AnswerEdge) => edge.node.id !== tempId
                ),
              },
            },
          });
        }

        throw error;
      }
    },
  };
};

/**
 * Hook to delete all answers for a user and case.
 *
 * Queries answers first, then deletes them individually.
 * Note: Backend doesn't have a bulk delete mutation for answers.
 */
export const useDeleteAnswers = () => {
  const apolloClient = useApolloClient();

  return {
    mutateAsync: async ({ userId, caseId }: { userId: string; caseId: string }) => {
      // Query answers for this user and case
      const { data } = await apolloClient.query({
        query: ListAnswersDocument,
        variables: {
          where: {
            userId: { equals: userId },
            patientCaseId: { equals: caseId },
            masterClassUserSessionId: { not: { equals: null } },
          },
        },
        fetchPolicy: 'network-only',
      });

      const answersToDelete = data?.listAnswers?.edges || [];

      // Delete each answer (backend doesn't have bulk delete)
      // For now, we'll just refetch the query to clear the cache
      // If a deleteAnswer mutation becomes available, we can use it here

      // Clear cache by evicting all matching answers
      if (answersToDelete.length > 0) {
        answersToDelete.forEach((edge: AnswerEdge) => {
          apolloClient.cache.evict({
            id: apolloClient.cache.identify({ __typename: 'Answer', id: edge.node.id }),
          });
        });
        apolloClient.cache.gc();
      }

      // Refetch to ensure consistency
      await apolloClient.refetchQueries({
        include: [ListAnswersDocument],
      });
    },
  };
};
